/**
 * @author      luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright   Copyright (c) 2023, Zumper
 * @description helpers for breadcrumbs
 */

import { PROPERTY_TYPE_GROUP_NAMES, PROPERTY_TYPE_NAME_TO_GROUP } from '@zcode-ui/filters'
import type { Property } from '@zcode/state-type'
import { routesConstants } from '@zcode/zumper-routes'

export type PropertyTypePath =
  | typeof routesConstants.APTS_BASE_URL
  | typeof routesConstants.CONDOS_BASE_URL
  | typeof routesConstants.HOUSES_BASE_URL
  | typeof routesConstants.ROOMS_BASE_URL

export const isHouseType = (propertyType: Property['propertyType']) => {
  return (
    !!propertyType &&
    (PROPERTY_TYPE_NAME_TO_GROUP[PROPERTY_TYPE_GROUP_NAMES.TOWNHOUSE].includes(propertyType) ||
      PROPERTY_TYPE_NAME_TO_GROUP[PROPERTY_TYPE_GROUP_NAMES.HOUSE].includes(propertyType))
  )
}

export const isRoomType = (propertyType: Property['propertyType']) => {
  return (
    !!propertyType &&
    PROPERTY_TYPE_NAME_TO_GROUP[PROPERTY_TYPE_GROUP_NAMES.ROOM].includes(propertyType)
  )
}

export const isCondoType = (propertyType: Property['propertyType']) => {
  return (
    !!propertyType &&
    PROPERTY_TYPE_NAME_TO_GROUP[PROPERTY_TYPE_GROUP_NAMES.CONDO].includes(propertyType)
  )
}

// We omit conods by default since /condos-for-rent URL don't seem to work for now
export const getPropertyTypeLabel = (propertyType: Property['propertyType'], omitCondos = true) => {
  if (isHouseType(propertyType)) {
    return 'Houses' as const
  }
  if (isRoomType(propertyType)) {
    return 'Rooms' as const
  }
  if (isCondoType(propertyType) && !omitCondos) {
    return 'Condos' as const
  }
  return 'Apartments' as const
}

export const getBaseUrlFromPropertyType = ({
  propertyType,
  propertyTypePath,
}: {
  propertyType: Property['propertyType']
  propertyTypePath?: PropertyTypePath
}) => {
  if (
    propertyTypePath &&
    [
      routesConstants.CONDOS_BASE_URL,
      routesConstants.HOUSES_BASE_URL,
      routesConstants.ROOMS_BASE_URL,
    ].includes(propertyTypePath)
  ) {
    return propertyTypePath
  }
  if (isHouseType(propertyType)) {
    return routesConstants.HOUSES_BASE_URL
  }
  if (isRoomType(propertyType)) {
    return routesConstants.ROOMS_BASE_URL
  }
  return routesConstants.APTS_BASE_URL
}
